import { useQuery } from "@tanstack/react-query";

import { useProject } from "../app/projectContext";
import { getSldElements, type SldElements } from "../services/sldElements";

// The elements of the active revision's single-line diagram, for the study pickers
// (SLD-b1, A27 (b), (e)). Read only while an open revision is selected in the top bar (the
// same condition under which a run is saved to a revision); a read-only revision, no
// project at all, or a revision whose diagram is still at version 0 give no elements, and
// the forms keep their typed codes exactly as today.

export const SLD_ELEMENTS_QUERY_KEY = ["sld", "elements"] as const;

export type UseSldElementsResult = {
  /** The diagram's elements, or null: no open revision, no diagram yet, still loading, failed. */
  elements: SldElements | null;
  /** The revision the elements belong to (the selected open revision), or null. */
  revisionId: string | null;
  isLoading: boolean;
  error: Error | null;
};

export function useSldElements(): UseSldElementsResult {
  const { state } = useProject();
  const scope =
    state.status === "selected"
      ? { projectId: state.project.id, revisionId: state.revision.id }
      : null;

  const query = useQuery({
    queryKey: [...SLD_ELEMENTS_QUERY_KEY, scope?.revisionId ?? null],
    queryFn: ({ signal }) =>
      getSldElements(scope?.projectId ?? "", scope?.revisionId ?? "", signal),
    enabled: scope !== null,
  });

  const data = scope !== null ? (query.data ?? null) : null;
  return {
    elements: data !== null && data.version >= 1 ? data : null,
    revisionId: scope?.revisionId ?? null,
    isLoading: scope !== null && query.isPending,
    error: query.error,
  };
}
